import { useSyncExternalStore } from 'react';
import type { PersonalizationSummary } from '../../../common/js/types';

// The page config's `personalization` is a snapshot from page load. When the user changes their
// preferences in place (the suggestion panel), the fresh summary is published here so every
// mounted AI Search UI on the page (sidebar and dashboard alike) shows it without a reload.
let latest: PersonalizationSummary | null | undefined;
const listeners: Set< () => void > = new Set();

function subscribe( listener: () => void ): () => void {
	listeners.add( listener );
	return () => listeners.delete( listener );
}

/**
 * Publishes a fresh personalization summary to every mounted AI Search UI.
 *
 * @param {PersonalizationSummary} summary The summary returned by the server after a change.
 */
export function setPersonalization( summary: PersonalizationSummary ): void {
	latest = summary;
	listeners.forEach( ( listener ) => listener() );
}

/**
 * The user's current personalization summary: the latest published one, or the page-load snapshot.
 *
 * @param {PersonalizationSummary|null} [initial] The page config's snapshot.
 * @return {PersonalizationSummary|null} The summary to show.
 */
export function usePersonalization( initial?: PersonalizationSummary | null ): PersonalizationSummary | null {
	const published = useSyncExternalStore( subscribe, () => latest );
	return published !== undefined ? published : ( initial ?? null );
}
